import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useEnquiryForm } from "@/context/FormContext";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ShieldCheck, Paperclip, FileCheck2, X } from "lucide-react";
import { submitEnquiry, uploadPitchDeck } from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";

// Nature of enquiry -> backend category (drives reference prefix + conditional fields)
const NATURES = [
  { value: "AI business seeking funding", category: "founder" },
  { value: "AI business seeking technology support", category: "founder" },
  { value: "Marketing and commercialisation support", category: "founder" },
  { value: "Manufacturing and product support", category: "founder" },
  { value: "AIF consultation", category: "investment" },
  { value: "PMS consultation", category: "investment" },
  { value: "Mutual fund consultation", category: "investment" },
  { value: "Bonds or fixed-income consultation", category: "investment" },
  { value: "Insurance consultation", category: "investment" },
  { value: "Strategic partnership", category: "partner" },
  { value: "General business enquiry", category: "general" },
  { value: "Grievance or data-rights request", category: "grievance" },
];

const BUSINESS_STAGES = ["Idea", "Research", "Prototype", "Proof of concept", "Pilot", "Pre-revenue", "Revenue generating", "Growth stage", "Manufacturing preparation", "Commercial manufacturing"];
const REVENUE = ["No revenue yet", "Under ₹1 lakh", "₹1–10 lakh", "₹10–50 lakh", "₹50 lakh – 1 crore", "Over ₹1 crore"];
const FUNDING = ["Under ₹25 lakh", "₹25 lakh – 1 crore", "₹1–5 crore", "₹5–25 crore", "Over ₹25 crore", "Not sure yet"];
const SUPPORT_OPTIONS = ["Capital readiness", "Technology", "Branding & marketing", "Sales & distribution", "Manufacturing & supply", "Talent & operations", "Legal & compliance", "International expansion"];
const INVESTOR_TYPES = ["Individual", "HUF", "Company", "LLP", "Partnership", "Trust", "Family office", "Institution", "NRI"];
const PRODUCT_INTEREST = ["AIF", "PMS", "Mutual funds", "Bonds / NCDs", "Fixed deposits", "Insurance", "REITs / InvITs", "Other approved products"];
const HORIZONS = ["< 1 year", "1–3 years", "3–5 years", "5+ years"];
const GRIEVANCE_TYPES = ["General grievance", "Application-data correction", "Consent withdrawal", "Data-access request", "Data-erasure request", "Document-removal request", "Marketing opt-out", "Security concern", "Financial-product complaint", "Other request"];

const field = "bg-[#0C0C10] border-white/10 text-[#F4F4F5] placeholder:text-zinc-600 focus-visible:ring-[#0057FF] h-11";

function defaultNatureFor(type) {
  const found = NATURES.find((n) => n.category === type);
  return found ? found.value : NATURES[0].value;
}

export default function SmartForm() {
  const { open, setOpen, defaultType, prefill } = useEnquiryForm();
  const [nature, setNature] = useState(defaultNatureFor(defaultType || "founder"));
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({});
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
    if (![".pdf", ".ppt", ".pptx", ".doc", ".docx", ".key"].includes(ext)) return toast.error("Please upload a PDF, PPT, PPTX, DOC, DOCX or KEY file.");
    if (file.size > 15 * 1024 * 1024) return toast.error("File too large. Maximum 15 MB.");
    setUploading(true);
    try {
      const data = await uploadPitchDeck(file);
      setForm((f) => ({ ...f, pitch_deck_file_id: data.file_id, pitch_deck_filename: data.filename }));
      toast.success("Pitch deck attached.");
    } catch {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };
  const removeFile = () => setForm((f) => ({ ...f, pitch_deck_file_id: undefined, pitch_deck_filename: undefined }));

  const category = NATURES.find((n) => n.value === nature)?.category || "general";

  useEffect(() => {
    if (open) {
      setNature(defaultNatureFor(defaultType || "founder"));
      setResult(null);
      setForm(prefill || {});
    }
  }, [open, defaultType, prefill]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArr = (k, v) => setForm((f) => {
    const arr = f[k] || [];
    return { ...f, [k]: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] };
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) return toast.error("Please provide your name and email.");
    if (!form.privacy_accepted || !form.declarations_accepted) return toast.error("Please accept the required consents to continue.");

    const common = ["full_name", "email", "mobile", "city", "state", "privacy_accepted", "declarations_accepted", "marketing_consent", "company_website_hp"];
    const details = { nature };
    Object.keys(form).forEach((k) => { if (!common.includes(k)) details[k] = form[k]; });

    setSubmitting(true);
    try {
      const data = await submitEnquiry({
        enquiry_type: category,
        full_name: form.full_name,
        email: form.email,
        mobile: form.mobile || null,
        city: form.city || null,
        state: form.state || null,
        details,
        privacy_accepted: !!form.privacy_accepted,
        declarations_accepted: !!form.declarations_accepted,
        marketing_consent: !!form.marketing_consent,
        company_website_hp: form.company_website_hp || "",
      });
      setResult({ ...data, nature });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const pill = (k, v) => {
    const active = (form[k] || []).includes(v);
    return <button type="button" key={v} onClick={() => toggleArr(k, v)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? "bg-[#0057FF] border-[#0057FF] text-white" : "border-white/10 text-zinc-400 hover:border-white/25"}`}>{v}</button>;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#08080B] border-white/10 text-[#F4F4F5] p-0" data-testid="enquiry-dialog">
        <DialogTitle className="sr-only">FundAI Secure Enquiry Form</DialogTitle>
        <DialogDescription className="sr-only">Select the nature of your enquiry and provide the relevant information.</DialogDescription>
        {result ? (
          <div className="p-10 text-center" data-testid="enquiry-success">
            <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 200 }} className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[#0057FF]/15">
              <CheckCircle2 className="h-9 w-9 text-[#00D1FF]" />
            </motion.div>
            <h3 className="font-head text-2xl font-medium">Your enquiry has been received</h3>
            <div className="mt-5 inline-flex flex-col items-center gap-1">
              <p className="font-mono text-xs text-zinc-500">Reference Number</p>
              <p className="font-mono text-xl text-[#00D1FF]" data-testid="enquiry-reference">{result.reference}</p>
            </div>
            <p className="mt-4 text-sm text-zinc-500">{result.nature} · {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
            <p className="mt-6 text-sm text-zinc-400 leading-relaxed max-w-md mx-auto">
              The FundAI team will review your submission and contact you if further information is required.
            </p>
            <p className="mt-4 text-xs text-zinc-600 leading-relaxed max-w-md mx-auto">
              This acknowledgement is not a funding approval, investment offer, product allotment, loan sanction, partnership approval or commercial commitment.
            </p>
            <Button onClick={() => setOpen(false)} className="mt-8 bg-[#0057FF] hover:bg-[#0047D6] text-white rounded-full px-8" data-testid="enquiry-close-btn">Close</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-8">
            <div className="mb-6">
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#00D1FF]">Start a Conversation</span>
              <h3 className="font-head text-2xl font-medium mt-2">Tell us about your enquiry</h3>
              <p className="text-sm text-zinc-500 mt-1">Select the nature of your enquiry and provide the relevant information. The FundAI team will review your submission and contact you if further details are required.</p>
            </div>

            <div className="space-y-5">
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Nature of enquiry</label>
                <Select value={nature} onValueChange={setNature}>
                  <SelectTrigger className={field} data-testid="enquiry-purpose-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0C0C10] border-white/10 text-[#F4F4F5] max-h-72">
                    {NATURES.map((p) => <SelectItem key={p.value} value={p.value} className="focus:bg-white/5 focus:text-white">{p.value}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Honeypot */}
              <input type="text" tabIndex={-1} autoComplete="off" value={form.company_website_hp || ""} onChange={(e) => set("company_website_hp", e.target.value)} className="hidden" aria-hidden="true" />

              <div className="grid md:grid-cols-2 gap-4">
                <Input className={field} placeholder="Full name *" value={form.full_name || ""} onChange={(e) => set("full_name", e.target.value)} data-testid="enquiry-name" />
                <Input className={field} type="email" placeholder="Email *" value={form.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="enquiry-email" />
                <Input className={field} placeholder="Mobile number" value={form.mobile || ""} onChange={(e) => set("mobile", e.target.value)} data-testid="enquiry-mobile" />
                <div className="grid grid-cols-2 gap-4">
                  <Input className={field} placeholder="City" value={form.city || ""} onChange={(e) => set("city", e.target.value)} />
                  <Input className={field} placeholder="State" value={form.state || ""} onChange={(e) => set("state", e.target.value)} />
                </div>
                <Input className={field} placeholder="Company / organisation" value={form.company || ""} onChange={(e) => set("company", e.target.value)} />
                <Input className={field} placeholder="Website (optional)" value={form.website || ""} onChange={(e) => set("website", e.target.value)} />
              </div>

              <AnimatePresence mode="wait">
                <motion.div key={category} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }} className="space-y-4">
                  {category === "founder" && (
                    <>
                      <div className="grid md:grid-cols-2 gap-4">
                        <Select value={form.business_stage || ""} onValueChange={(v) => set("business_stage", v)}>
                          <SelectTrigger className={field}><SelectValue placeholder="Business stage" /></SelectTrigger>
                          <SelectContent className="bg-[#0C0C10] border-white/10 text-[#F4F4F5] max-h-64">{BUSINESS_STAGES.map((s) => <SelectItem key={s} value={s} className="focus:bg-white/5 focus:text-white">{s}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={form.revenue || ""} onValueChange={(v) => set("revenue", v)}>
                          <SelectTrigger className={field}><SelectValue placeholder="Monthly revenue range" /></SelectTrigger>
                          <SelectContent className="bg-[#0C0C10] border-white/10 text-[#F4F4F5] max-h-64">{REVENUE.map((s) => <SelectItem key={s} value={s} className="focus:bg-white/5 focus:text-white">{s}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={form.amount_sought || ""} onValueChange={(v) => set("amount_sought", v)}>
                          <SelectTrigger className={field}><SelectValue placeholder="Funding required" /></SelectTrigger>
                          <SelectContent className="bg-[#0C0C10] border-white/10 text-[#F4F4F5] max-h-64">{FUNDING.map((s) => <SelectItem key={s} value={s} className="focus:bg-white/5 focus:text-white">{s}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input className={field} placeholder="Previous capital raised (optional)" value={form.previous_capital || ""} onChange={(e) => set("previous_capital", e.target.value)} />
                      </div>
                      <Input className={field} placeholder="Proposed use of funds" value={form.use_of_funds || ""} onChange={(e) => set("use_of_funds", e.target.value)} />
                      <Textarea className={field + " h-24 py-3"} placeholder="Short description — problem, solution and role of AI." value={form.description || ""} onChange={(e) => set("description", e.target.value)} />
                      <div>
                        <label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Execution support required</label>
                        <div className="flex flex-wrap gap-2">{SUPPORT_OPTIONS.map((s) => pill("support", s))}</div>
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Pitch deck (optional)</label>
                        {form.pitch_deck_filename ? (
                          <div className="flex items-center justify-between gap-3 rounded-lg border border-[#0057FF]/40 bg-[#0057FF]/5 px-4 py-3" data-testid="pitch-deck-attached">
                            <span className="flex items-center gap-2 text-sm text-zinc-300 truncate"><FileCheck2 className="h-4 w-4 text-[#00D1FF] shrink-0" /> {form.pitch_deck_filename}</span>
                            <button type="button" onClick={removeFile} className="text-zinc-500 hover:text-white shrink-0" aria-label="Remove file" data-testid="pitch-deck-remove"><X className="h-4 w-4" /></button>
                          </div>
                        ) : (
                          <label className={`flex items-center gap-3 rounded-lg border border-dashed border-white/15 hover:border-[#0057FF]/50 bg-[#0C0C10] px-4 py-3 cursor-pointer transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="pitch-deck-dropzone">
                            {uploading ? <Loader2 className="h-4 w-4 animate-spin text-[#00D1FF]" /> : <Paperclip className="h-4 w-4 text-zinc-500" />}
                            <span className="text-sm text-zinc-500">{uploading ? "Uploading…" : "Attach your pitch deck — PDF, PPT, DOC (max 15 MB)"}</span>
                            <input type="file" accept=".pdf,.ppt,.pptx,.doc,.docx,.key" className="hidden" onChange={handleFile} data-testid="pitch-deck-input" />
                          </label>
                        )}
                        <p className="mt-2 text-[11px] text-zinc-600 leading-relaxed">Please share only non-confidential materials. Do not upload trade secrets or highly sensitive information at this stage. Files are stored securely and are never publicly accessible.</p>
                      </div>
                    </>
                  )}

                  {category === "investment" && (
                    <>
                      <div className="grid md:grid-cols-2 gap-4">
                        <Select value={form.investor_type || ""} onValueChange={(v) => set("investor_type", v)}>
                          <SelectTrigger className={field}><SelectValue placeholder="Investor type" /></SelectTrigger>
                          <SelectContent className="bg-[#0C0C10] border-white/10 text-[#F4F4F5] max-h-64">{INVESTOR_TYPES.map((s) => <SelectItem key={s} value={s} className="focus:bg-white/5 focus:text-white">{s}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input className={field} placeholder="Approx. investment range" value={form.investment_range || ""} onChange={(e) => set("investment_range", e.target.value)} />
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Products of interest</label>
                        <div className="flex flex-wrap gap-2">{PRODUCT_INTEREST.map((s) => pill("product_interest", s))}</div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <Select value={form.horizon || ""} onValueChange={(v) => set("horizon", v)}>
                          <SelectTrigger className={field}><SelectValue placeholder="Investment horizon" /></SelectTrigger>
                          <SelectContent className="bg-[#0C0C10] border-white/10 text-[#F4F4F5]">{HORIZONS.map((s) => <SelectItem key={s} value={s} className="focus:bg-white/5 focus:text-white">{s}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input className={field} placeholder="Preferred consultation time" value={form.preferred_time || ""} onChange={(e) => set("preferred_time", e.target.value)} />
                      </div>
                      <p className="text-xs text-zinc-500 leading-relaxed rounded-lg border border-white/10 p-3">
                        This form records only your request for information or consultation. It is not an investment application, offer, recommendation, allotment, transaction confirmation or guarantee of product access.
                      </p>
                    </>
                  )}

                  {category === "partner" && (
                    <>
                      <div className="grid md:grid-cols-2 gap-4">
                        <Input className={field} placeholder="Organisation type" value={form.organisation_type || ""} onChange={(e) => set("organisation_type", e.target.value)} />
                        <Input className={field} placeholder="Geographic coverage" value={form.coverage || ""} onChange={(e) => set("coverage", e.target.value)} />
                      </div>
                      <Input className={field} placeholder="Capabilities offered" value={form.capabilities || ""} onChange={(e) => set("capabilities", e.target.value)} />
                      <Textarea className={field + " h-24 py-3"} placeholder="Relevant experience and proposed partnership." value={form.proposal || ""} onChange={(e) => set("proposal", e.target.value)} />
                      <p className="text-xs text-zinc-500 leading-relaxed rounded-lg border border-white/10 p-3">
                        Submitting a partnership enquiry does not create an agency, partnership, joint venture, exclusivity, fiduciary relationship, authority to represent FundAI or commercial obligation.
                      </p>
                    </>
                  )}

                  {category === "general" && (
                    <Textarea className={field + " h-28 py-3"} placeholder="How can we help?" value={form.description || ""} onChange={(e) => set("description", e.target.value)} />
                  )}

                  {category === "grievance" && (
                    <>
                      <Select value={form.request_type || ""} onValueChange={(v) => set("request_type", v)}>
                        <SelectTrigger className={field}><SelectValue placeholder="Type of request" /></SelectTrigger>
                        <SelectContent className="bg-[#0C0C10] border-white/10 text-[#F4F4F5] max-h-64">{GRIEVANCE_TYPES.map((s) => <SelectItem key={s} value={s} className="focus:bg-white/5 focus:text-white">{s}</SelectItem>)}</SelectContent>
                      </Select>
                      <Textarea className={field + " h-28 py-3"} placeholder="Describe your request or concern." value={form.description || ""} onChange={(e) => set("description", e.target.value)} />
                      <p className="text-xs text-zinc-500 leading-relaxed">Product-specific complaints may need to be forwarded to the relevant registered provider or authorised intermediary.</p>
                    </>
                  )}

                  <Input className={field} placeholder="Preferred contact time (optional)" value={form.preferred_contact_time || ""} onChange={(e) => set("preferred_contact_time", e.target.value)} />
                </motion.div>
              </AnimatePresence>

              {/* Consent */}
              <div className="space-y-3 border-t border-white/10 pt-5">
                <label className="flex gap-3 items-start cursor-pointer">
                  <Checkbox checked={!!form.declarations_accepted} onCheckedChange={(v) => set("declarations_accepted", v)} className="mt-0.5 border-white/30 data-[state=checked]:bg-[#0057FF] data-[state=checked]:border-[#0057FF]" data-testid="consent-declarations" />
                  <span className="text-xs text-zinc-400 leading-relaxed">The information submitted is accurate to my knowledge. I understand submission does not guarantee funding, investment access, an introduction, a term sheet, a loan or commercial engagement, and that no binding obligation exists until definitive written agreements are signed.</span>
                </label>
                <label className="flex gap-3 items-start cursor-pointer">
                  <Checkbox checked={!!form.privacy_accepted} onCheckedChange={(v) => set("privacy_accepted", v)} className="mt-0.5 border-white/30 data-[state=checked]:bg-[#0057FF] data-[state=checked]:border-[#0057FF]" data-testid="consent-privacy" />
                  <span className="text-xs text-zinc-400 leading-relaxed">I have reviewed the Privacy Notice and consent to FundAI contacting me and processing my information for the stated purposes. My data will not be used to train publicly available AI models.</span>
                </label>
                <label className="flex gap-3 items-start cursor-pointer">
                  <Checkbox checked={!!form.marketing_consent} onCheckedChange={(v) => set("marketing_consent", v)} className="mt-0.5 border-white/30 data-[state=checked]:bg-[#0057FF] data-[state=checked]:border-[#0057FF]" data-testid="consent-marketing" />
                  <span className="text-xs text-zinc-500 leading-relaxed">Optional: I would like to receive occasional updates from FundAI. (Not required to submit.)</span>
                </label>
              </div>

              <Button type="submit" disabled={submitting} className="w-full h-12 bg-[#0057FF] hover:bg-[#0047D6] text-white rounded-full font-medium active:scale-[0.98] transition-transform" data-testid="enquiry-submit-btn">
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Submit Enquiry"}
              </Button>
              <p className="flex items-center justify-center gap-2 text-[11px] text-zinc-600"><ShieldCheck className="h-3.5 w-3.5" /> Encrypted in transit · Stored securely · No payment is collected</p>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
